import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
  Autocomplete,
  CrossIcon,
  IconButton,
  SideSheet,
  Spinner,
  TextInputField,
  toaster,
} from "evergreen-ui";
import React, { useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import Button from "src/Components/Button";
import InfoBox from "src/Components/InfoBox";

export const GET_COUNTRIES = gql`
  {
    country {
      id
      name
      language
      status
      currency
      retail_price
      conversion
      users_aggregate {
        aggregate {
          count(columns: id)
        }
      }
    }
  }
`;
export const GET_SHIPPING = gql`
  {
    shipping_rate {
      id
      name
      country
      rate
      status
      shipping_rate_country {
        id
        name
        currency
      }
    }
  }
`;

const INSERT_COUNTRY = gql`
  mutation newCountry($object: country_insert_input!) {
    insert_country_one(object: $object) {
      id
      name
      language
      status
      currency
      retail_price
      conversion
      users_aggregate {
        aggregate {
          count(columns: id)
        }
      }
      __typename
    }
  }
`;

const UPDATE_COUNTRY = gql`
  mutation updateCountry($id: Int!, $set: country_set_input) {
    update_country_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
      name
      language
      status
      currency
      retail_price
      conversion
      users_aggregate {
        aggregate {
          count(columns: id)
        }
      }
      __typename
    }
  }
`;

const INSERT_SHIPPING = gql`
  mutation insertShipping($object: shipping_rate_insert_input!) {
    insert_shipping_rate_one(object: $object) {
      id
      name
      country
      rate
      status
      shipping_rate_country {
        id
        name
        currency
      }
    }
  }
`;

const UPDATE_SHIPPING = gql`
  mutation updateShipping($id: Int!, $set: shipping_rate_set_input!) {
    update_shipping_rate_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
      name
      country
      rate
      status
      shipping_rate_country {
        id
        name
        currency
      }
    }
  }
`;

const DELETE_SHIPPING = gql`
  mutation deleteShipping($id: Int!) {
    delete_shipping_rate_by_pk(id: $id) {
      __typename
      id
    }
  }
`;
interface IProps extends RouteComponentProps<any> {}
const CountriesShipping: React.FC<IProps> = () => {
  const apolloClient = useApolloClient();

  const {
    data: countryData,
    loading: countryLoading,
    error: countryError,
  } = useQuery(GET_COUNTRIES);
  const {
    data: shippingData,
    loading: shippingLoading,
    error: shippingError,
  } = useQuery(GET_SHIPPING);
  const [newCountry, { loading: newCountryLoading }] = useMutation(
    INSERT_COUNTRY,
    {
      update: (_, { data: updateCountryData }) => {
        const queryData = apolloClient.readQuery({
          query: GET_COUNTRIES,
        });
        const newCountry = updateCountryData.insert_country_one;
        const newData = {
          country: [newCountry, ...queryData.country],
        };
        apolloClient.writeQuery({
          query: GET_COUNTRIES,
          data: newData,
        });
      },
    }
  );
  const [updateCountry, { loading: updateCountryLoading }] = useMutation(
    UPDATE_COUNTRY
  );

  const [newShipping, { loading: newShippingLoading }] = useMutation(
    INSERT_SHIPPING,
    {
      update: (_, { data: updateShippingData }) => {
        const queryData = apolloClient.readQuery({
          query: GET_SHIPPING,
        });
        const newShipping = updateShippingData.insert_shipping_rate_one;
        const newData = {
          shipping_rate: [newShipping, ...queryData.shipping_rate],
        };
        apolloClient.writeQuery({
          query: GET_SHIPPING,
          data: newData,
        });
      },
    }
  );
  const [updateShipping, { loading: updateShippingLoading }] = useMutation(
    UPDATE_SHIPPING
  );
  const [deleteShipping] = useMutation(DELETE_SHIPPING, {
    update: (_, { data: { delete_shipping_rate_by_pk } }) => {
      const queryData = apolloClient.readQuery({
        query: GET_SHIPPING,
      });

      const newShipping = queryData!.shipping_rate.filter(
        (ship) => ship.id !== showShipping?.id
      );
      apolloClient.writeQuery({
        query: GET_SHIPPING,
        data: { shipping_rate: newShipping },
      });
    },
  });

  const [showCountry, setShowCountry] = useState<any>(null);
  const [showShipping, setShowShipping] = useState<any>(null);

  const onCountrySave = (e) => {
    e.preventDefault();
    if (!newCountryLoading && !updateCountryLoading) {
      if (showCountry?.id) {
        updateCountry({
          variables: {
            id: showCountry.id,
            set: {
              name: showCountry.name,
              currency: showCountry.currency,
              retail_price: showCountry.retail_price,
            },
          },
        }).then(() => {
          toaster.success("Changes saved!");
        });
      } else {
        newCountry({
          variables: {
            object: {
              name: showCountry.name,
              currency: showCountry.currency,
              retail_price: showCountry.retail_price,
            },
          },
        }).then(() => {
          toaster.success("Country added!");
        });
      }
    }
  };

  const onShippingSave = (e) => {
    e.preventDefault();
    if (!newShippingLoading && !updateShippingLoading) {
      if (Boolean(showShipping?.id)) {
        updateShipping({
          variables: {
            id: showShipping.id,
            set: {
              name: showShipping.name,
              country: showShipping.country,
              rate: showShipping.rate,
            },
          },
        })
          .then(() => {
            toaster.success("Changes saved!");
          })
          .catch((e) => {
            toaster.danger(e.message);
          });
      } else {
        newShipping({
          variables: {
            object: {
              name: showShipping.name,
              country: showShipping.country,
              rate: showShipping.rate,
            },
          },
        })
          .then(() => {
            toaster.success("Shipping created!");
          })
          .catch((e) => {
            toaster.danger(e.message);
          });
      }
    }
  };

  const onShippingDelete = () => {
    deleteShipping({
      variables: {
        id: showShipping?.id,
      },
    })
      .then(() => {
        toaster.success("Shipping method deleted.");
        setShowShipping(null);
      })
      .catch((e) => {
        toaster.danger(e.message);
      });
  };
  return (
    <div className=" admin-page-container pb-96">
      <div className=" admin-page-header">
        <h1 className=" text-lg font-bold text-gray-800">
          Countries and Shipping method
        </h1>
      </div>
      <InfoBox
        title="Countries"
        description="Currently we only allow 1 retail pricing for each countries. After adding a new country, remember to create packages according to your marketing plan so your agents can see it! Members are only allowed to join countries that are listed here."
        content={
          <div className=" w-full">
            {countryLoading && <Spinner />}
            {countryError?.message}
            {countryData?.country?.length > 0
              ? countryData.country.map((cou, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      setShowCountry({ ...cou, show: cou.name });
                    }}
                    className=" flex flex-row hover-basic p-3 w-full justify-between"
                  >
                    <p className=" font-semibold text-gray-700">
                      {cou.name}
                      <span className=" font-thin"></span>
                    </p>
                    <p>{cou.users_aggregate.aggregate?.count} agents</p>
                  </div>
                ))
              : "No country yet."}
            <p
              onClick={() => {
                setShowCountry({
                  name: null,
                });
              }}
              className=" w-full text-center px-4 py-2 hover:text-blue-800 cursor-pointer text-blue-400 mb-4"
            >
              + Add a country
            </p>
          </div>
        }
      />
      <InfoBox
        title="Shipping method"
        description="Currently shipping is only available by order. More shipment methods will be available soon."
        content={
          <div className=" w-full">
            {shippingLoading && <Spinner />}
            {shippingError?.message}
            {shippingData?.shipping_rate?.length > 0
              ? shippingData.shipping_rate.map((ship, index) => (
                  <div
                    key={index}
                    className=" flex flex-row hover-basic p-3 w-full justify-between"
                    onClick={() => {
                      setShowShipping({ ...ship, show: ship.name });
                    }}
                  >
                    <p className=" font-semibold text-gray-700">
                      {ship.name}{" "}
                      <span className=" font-thin">
                        ({ship.shipping_rate_country.name})
                      </span>
                    </p>
                    <p>
                      {ship.shipping_rate_country.currency}
                      {ship.rate}
                    </p>
                  </div>
                ))
              : "No shipping rate found."}
            <p
              onClick={() => {
                setShowShipping({
                  show: null,
                });
              }}
              className=" w-full text-center px-4 py-2 hover:text-blue-800 cursor-pointer text-blue-400 mb-4"
            >
              + Add a shipping rate
            </p>
          </div>
        }
      />
      <SideSheet
        onCloseComplete={() => {
          setShowCountry(null);
        }}
        preventBodyScrolling
        containerProps={{
          display: "flex",
          flex: "1",
          flexDirection: "column",
          maxWidth: "100vw",
        }}
        isShown={Boolean(showCountry)}
      >
        <div className=" w-full p-3 bg-white flex flex-row justify-between">
          <h1 className=" font-semibold text-lg">
            {showCountry?.show || "New country"}
          </h1>
          <IconButton
            icon={CrossIcon}
            onClick={() => {
              setShowCountry(null);
            }}
          />
        </div>
        <div className=" w-full flex h-full flex-auto flex-col bg-gray-50 p-3">
          <form onSubmit={onCountrySave} className=" w-full p-3 bg-white ">
            <TextInputField
              label="Name"
              value={showCountry?.name}
              placeholder="Malaysia, Singapore, Hong Kong..."
              onChange={(e) => {
                setShowCountry({ ...showCountry, name: e.target.value });
              }}
            />
            <TextInputField
              label="Currency"
              value={showCountry?.currency}
              placeholder="RM, SGD, HKD..."
              onChange={(e) => {
                setShowCountry({ ...showCountry, currency: e.target.value });
              }}
            />
            <TextInputField
              label="Retail Price"
              value={showCountry?.retail_price}
              onChange={(e) => {
                setShowCountry({
                  ...showCountry,
                  retail_price: e.target.value,
                });
              }}
            />
            <Button
              disabled={newCountryLoading || updateCountryLoading}
              label={
                newCountryLoading || updateCountryLoading
                  ? "Loading..."
                  : showCountry?.show
                  ? "Save"
                  : "Create Country"
              }
              onClick={onCountrySave}
            />
          </form>
        </div>
      </SideSheet>
      <SideSheet
        isShown={Boolean(showShipping)}
        onCloseComplete={() => {
          setShowShipping(null);
        }}
      >
        <div className=" w-full p-3 bg-white flex flex-row justify-between">
          <h1 className=" font-semibold text-lg">
            {showShipping?.show || "New shipping method"}
          </h1>
          <IconButton
            icon={CrossIcon}
            onClick={() => {
              setShowShipping(null);
            }}
          />
        </div>
        <div className=" w-full flex h-full flex-auto flex-col bg-gray-50 p-3">
          <form onSubmit={onShippingSave} className=" w-full p-3 bg-white ">
            <TextInputField
              value={showShipping?.name}
              label="Shipping Name"
              placeholder={"e.g. West Malaysia"}
              onChange={(e) => {
                setShowShipping({ ...showShipping, name: e.target.value });
              }}
            />
            <TextInputField
              value={showShipping?.rate}
              label="Shipping Rate"
              placeholder={"e.g. 0"}
              onChange={(e) => {
                setShowShipping({ ...showShipping, rate: e.target.value });
              }}
            />
            <div className=" w-full pb-4">
              <Autocomplete
                title="Country"
                onChange={(changedItem) => {
                  let selectedCountryIndex = countryData?.country?.findIndex(
                    (a) => a.name === changedItem
                  );
                  let selectedCountry =
                    countryData?.country[selectedCountryIndex];
                  setShowShipping({
                    ...showShipping,
                    country: selectedCountry.id,
                  });
                }}
                items={countryData?.country?.map((c) => c.name)}
              >
                {(props) => {
                  const {
                    getInputProps,
                    getRef,
                    inputValue: propsInput,
                    openMenu,
                  } = props;
                  return (
                    <TextInputField
                      label="Country"
                      placeholder="Choose a country"
                      hint="Only agents registered within this country will see this shipping method."
                      ref={getRef}
                      {...getInputProps({
                        onFocus: () => {
                          openMenu();
                        },
                      })}
                      value={propsInput}
                    />
                  );
                }}
              </Autocomplete>
            </div>
            <Button
              disabled={
                newShippingLoading ||
                updateShippingLoading ||
                !showShipping?.name ||
                !showShipping?.country ||
                !showShipping?.rate
              }
              label={
                newShippingLoading || updateShippingLoading
                  ? "Loading..."
                  : showShipping?.show
                  ? "Save"
                  : "Create Shipping"
              }
              onClick={onShippingSave}
            />
            {showShipping?.id && (
              <div
                onClick={onShippingDelete}
                className=" w-full bg-red-500 text-white p-3 rounded-full text-center mt-3 cursor-pointer hover:opacity-70"
              >
                Delete
              </div>
            )}
          </form>
        </div>
      </SideSheet>
    </div>
  );
};

export default withRouter(CountriesShipping);
